/* ============================================================
   BlockStyleEditor — Per-block visual style customizer
   ─────────────────────────────────────────────────────────
   Connects to CanvasBlock.styles for individual block styling.
   Provides colour pickers, sliders, font selectors, and
   shadow presets with live preview.
   ============================================================ */

import { useCallback, type CSSProperties } from 'react'
import { Paintbrush, ChevronRight, RotateCcw } from 'lucide-react'
import { type CanvasBlock, type BlockStyles, getCardDef } from '@/lib/cms/card-registry'

interface BlockStyleEditorProps {
  block: CanvasBlock | null
  onUpdateStyles: (id: string, styles: BlockStyles) => void
}

// ── Constants ──────────────────────────────────────────

const DEFAULT_STYLES: BlockStyles = {
  backgroundColor: '',
  borderRadius: 12,
  borderColor: '',
  borderWidth: 0,
  padding: 0,
  opacity: 100,
  textColor: '',
  fontFamily: '',
  fontSize: 0,
  fontWeight: 400,
  textAlign: 'left',
  shadow: 'none',
}

const PRESET_COLORS = [
  '#ffffff', '#000000', '#0c0c18', '#1a1a2e',
  '#f43f5e', '#ec4899', '#a855f7', '#8b5cf6',
  '#6366f1', '#3b82f6', '#06b6d4', '#14b8a6',
  '#22c55e', '#84cc16', '#eab308', '#f97316',
  '#ef4444', '#00d1ff',
  'rgba(255,255,255,0.1)', 'rgba(255,255,255,0.05)',
  'transparent',
]

const FONT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'Inter', label: 'Inter' },
  { value: 'DM Sans', label: 'DM Sans' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Outfit', label: 'Outfit' },
]

const SHADOW_PRESETS: { value: BlockStyles['shadow']; label: string; css: string }[] = [
  { value: 'none', label: 'None', css: 'none' },
  { value: 'sm', label: 'Small', css: '0 1px 3px rgba(0,0,0,0.3)' },
  { value: 'md', label: 'Medium', css: '0 4px 12px rgba(0,0,0,0.4)' },
  { value: 'lg', label: 'Large', css: '0 8px 30px rgba(0,0,0,0.5)' },
  { value: 'glow', label: 'Glow', css: '0 0 20px rgba(0,209,255,0.15), 0 4px 12px rgba(0,0,0,0.3)' },
]

/** Converts BlockStyles → CSSProperties */
// eslint-disable-next-line react-refresh/only-export-components
export function blockStylesToCss(s?: BlockStyles): CSSProperties {
  if (!s) return {}
  const css: CSSProperties = {}
  if (s.backgroundColor) css.backgroundColor = s.backgroundColor
  if (s.borderRadius) css.borderRadius = `${s.borderRadius}px`
  if (s.borderColor) css.borderColor = s.borderColor
  if (s.borderWidth) { css.borderWidth = `${s.borderWidth}px`; css.borderStyle = 'solid' }
  if (s.padding) css.padding = `${s.padding}px`
  if (s.opacity && s.opacity < 100) css.opacity = s.opacity / 100
  if (s.textColor) css.color = s.textColor
  if (s.fontFamily) css.fontFamily = `"${s.fontFamily}", sans-serif`
  if (s.fontSize) css.fontSize = `${s.fontSize}px`
  if (s.fontWeight && s.fontWeight !== 400) css.fontWeight = s.fontWeight
  if (s.textAlign && s.textAlign !== 'left') css.textAlign = s.textAlign
  if (s.shadow && s.shadow !== 'none') {
    const preset = SHADOW_PRESETS.find(p => p.value === s.shadow)
    if (preset) css.boxShadow = preset.css
  }
  return css
}

// ── Component ──────────────────────────────────────────

export default function BlockStyleEditor({
  block,
  onUpdateStyles,
}: BlockStyleEditorProps) {
  const styles = block?.styles ?? DEFAULT_STYLES

  const update = useCallback(
    <K extends keyof BlockStyles>(key: K, value: BlockStyles[K]) => {
      if (!block) return
      onUpdateStyles(block.id, { ...styles, [key]: value })
    },
    [block, styles, onUpdateStyles]
  )

  const handleReset = useCallback(() => {
    if (!block) return
    onUpdateStyles(block.id, { ...DEFAULT_STYLES })
  }, [block, onUpdateStyles])

  if (!block) {
    return (
      <div className="editor-empty-state">
        <Paintbrush size={24} className="editor-empty-state-icon" />
        <div className="editor-empty-state-text">No block selected</div>
        <div className="editor-empty-state-hint">Click a block to style it</div>
      </div>
    )
  }

  const def = getCardDef(block.type)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Block header */}
      <div style={{
        padding: '8px var(--editor-panel-pad)',
        borderBottom: '1px solid var(--editor-border-dim)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>{def?.icon || '📄'}</span>
          <div>
            <div style={{ fontSize: 'var(--editor-font-size)', fontWeight: 600, color: 'var(--editor-text)' }}>
              {def?.label || block.type}
            </div>
            <div style={{ fontSize: 'var(--editor-font-size-xs)', fontFamily: 'var(--editor-font-mono)', color: 'var(--editor-text-dim)' }}>
              Style
            </div>
          </div>
        </div>
        <button className="editor-btn editor-btn-ghost editor-btn-sm" onClick={handleReset}>
          <RotateCcw size={10} />
          <span>Reset</span>
        </button>
      </div>

      {/* Style controls */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* ── Typography ── */}
        <StyleSection title="Typography">
          <ColorSwatchPicker label="Text Color" value={styles.textColor || ''} onChange={v => update('textColor', v)} />
          <div className="editor-row">
            <label htmlFor="bse-font" className="editor-label">Font</label>
            <select
              id="bse-font"
              className="editor-input"
              value={styles.fontFamily || ''}
              onChange={e => update('fontFamily', e.target.value)}
            >
              {FONT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <SliderRow label="Size" value={styles.fontSize ?? 0} min={0} max={48} unit="px" onChange={v => update('fontSize', v)} />
          <SliderRow label="Weight" value={styles.fontWeight ?? 400} min={100} max={900} step={100} unit="" onChange={v => update('fontWeight', v)} />
          <div className="editor-row">
            <label htmlFor="bse-align" className="editor-label">Alignment</label>
            <div id="bse-align" style={{ display: 'flex', gap: 4 }}>
              {(['left', 'center', 'right'] as const).map(al => (
                <button
                  key={al}
                  onClick={() => update('textAlign', al)}
                  className={`editor-btn editor-btn-sm ${styles.textAlign === al ? 'editor-btn-primary' : 'editor-btn-ghost'}`}
                  style={{ flex: 1, fontSize: 10 }}
                >
                  {al === 'left' ? '◀' : al === 'center' ? '◆' : '▶'}
                </button>
              ))}
            </div>
          </div>
        </StyleSection>

        {/* ── Border & Radius ── */}
        <StyleSection title="Border & Radius">
          <SliderRow label="Radius" value={styles.borderRadius ?? 12} min={0} max={32} unit="px" onChange={v => update('borderRadius', v)} />
          <SliderRow label="Width" value={styles.borderWidth ?? 0} min={0} max={6} unit="px" onChange={v => update('borderWidth', v)} />
          {(styles.borderWidth ?? 0) > 0 && (
            <ColorSwatchPicker label="Border Color" value={styles.borderColor || ''} onChange={v => update('borderColor', v)} />
          )}
        </StyleSection>

        {/* ── Effects ── */}
        <StyleSection title="Effects">
          <ColorSwatchPicker label="Background" value={styles.backgroundColor || ''} onChange={v => update('backgroundColor', v)} />
          <SliderRow label="Opacity" value={styles.opacity ?? 100} min={10} max={100} unit="%" onChange={v => update('opacity', v)} />
          <SliderRow label="Padding" value={styles.padding ?? 0} min={0} max={48} unit="px" onChange={v => update('padding', v)} />
          <div className="editor-row">
            <label htmlFor="bse-shadow" className="editor-label">Shadow</label>
            <div id="bse-shadow" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {SHADOW_PRESETS.map(p => (
                <button
                  key={p.value}
                  onClick={() => update('shadow', p.value)}
                  className={`editor-btn editor-btn-sm ${styles.shadow === p.value ? 'editor-btn-primary' : 'editor-btn-ghost'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="editor-row">
            <label htmlFor="bse-animation" className="editor-label">Animation</label>
            <div id="bse-animation" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {([
                { value: 'none', label: 'None' },
                { value: 'fadeIn', label: 'Fade In' },
                { value: 'slideUp', label: 'Slide Up' },
                { value: 'slideLeft', label: 'Slide Left' },
                { value: 'scaleIn', label: 'Scale In' },
                { value: 'bounce', label: 'Bounce' },
              ] as const).map(a => (
                <button
                  key={a.value}
                  onClick={() => update('animation', a.value)}
                  className={`editor-btn editor-btn-sm ${(styles.animation || 'none') === a.value ? 'editor-btn-primary' : 'editor-btn-ghost'}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </StyleSection>

        {/* ── Visibility ── */}
        <StyleSection title="Visibility">
          <div className="editor-row">
            <label htmlFor="bse-overflow" className="editor-label">Overflow</label>
            <select id="bse-overflow" className="editor-input" defaultValue="visible" disabled>
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
              <option value="auto">Auto</option>
            </select>
          </div>
          <div className="editor-row">
            <label htmlFor="bse-cursor" className="editor-label">Cursor</label>
            <select id="bse-cursor" className="editor-input" defaultValue="default" disabled>
              <option value="default">Default</option>
              <option value="pointer">Pointer</option>
              <option value="text">Text</option>
            </select>
          </div>
          <div className="editor-row">
            <label htmlFor="bse-pointer-events" className="editor-label">Pointer Events</label>
            <select id="bse-pointer-events" className="editor-input" defaultValue="auto" disabled>
              <option value="auto">Auto</option>
              <option value="none">None</option>
            </select>
          </div>
        </StyleSection>
      </div>
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────

function StyleSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderBottom: '1px solid var(--editor-border-dim)' }}>
      <div style={{
        padding: '6px var(--editor-panel-pad)',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <ChevronRight size={10} style={{ color: 'var(--editor-text-dim)', transform: 'rotate(90deg)' }} />
        <span className="editor-section-title">{title}</span>
      </div>
      <div className="editor-section-body" style={{ paddingTop: 0 }}>
        {children}
      </div>
    </div>
  )
}

function SliderRow({
  label, value, min, max, step = 1, unit, onChange,
}: {
  label: string; value: number; min: number; max: number; step?: number; unit: string
  onChange: (v: number) => void
}) {
  return (
    <div className="editor-row">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <label className="editor-label" style={{ marginBottom: 0 }}>{label}</label>
        <span style={{
          fontSize: 'var(--editor-font-size-xs)',
          fontFamily: 'var(--editor-font-mono)',
          color: 'var(--editor-text-dim)',
        }}>{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="editor-slider"
      />
    </div>
  )
}

function ColorSwatchPicker({
  label, value, onChange,
}: {
  label: string; value: string; onChange: (v: string) => void
}) {
  return (
    <div className="editor-row">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="editor-label" style={{ marginBottom: 0 }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 14, height: 14, borderRadius: 3,
            border: '1px solid var(--editor-border)',
            backgroundColor: value || 'transparent',
          }} />
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="—"
            style={{
              width: 72, textAlign: 'right',
              fontSize: 'var(--editor-font-size-xs)',
              fontFamily: 'var(--editor-font-mono)',
              color: 'var(--editor-text-dim)',
              background: 'transparent', border: 'none', outline: 'none',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {PRESET_COLORS.map(c => (
          <button
            key={c}
            onClick={() => onChange(c)}
            style={{
              width: 18, height: 18, borderRadius: 3,
              backgroundColor: c,
              border: c === 'transparent' ? '1px dashed var(--editor-border)' : '1px solid var(--editor-border-dim)',
              outline: value === c ? '2px solid var(--editor-accent)' : 'none',
              outlineOffset: 1,
              cursor: 'pointer',
              transition: 'transform 0.1s ease',
            }}
            title={c}
          />
        ))}
      </div>
    </div>
  )
}
